import { Injectable } from '@angular/core';

export interface LocalSession {
  phoneNumber: string;
  uid: string;
  displayName: string;
  email?: string;
  expiresAt: string; // ISO string
  createdAt: string;
  lastLoginAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class LocalSessionService {
  
  private readonly SESSION_KEY = 'parkbot_user_session';
  private readonly SESSION_DURATION_DAYS = 30;

  constructor() { }

  /**
   * Check if there's a valid local session for the phone number
   */
  hasValidSession(phoneNumber: string): LocalSession | null {
    try {
      const sessionData = localStorage.getItem(this.SESSION_KEY);
      if (!sessionData) return null;

      const session: LocalSession = JSON.parse(sessionData);
      
      // Check if session is for the same phone number
      if (session.phoneNumber !== phoneNumber) {
        return null;
      }

      // Check if session is expired
      const expiresAt = new Date(session.expiresAt);
      if (new Date() > expiresAt) {
        this.clearSession();
        return null;
      }

      return session;
    } catch (error) {
      console.error('Error checking local session:', error);
      this.clearSession();
      return null;
    }
  }

  /**
   * Create and store a local session
   */
  createSession(phoneNumber: string, uid: string, displayName: string, email?: string): LocalSession {
    const now = new Date();
    const expiresAt = new Date();
    expiresAt.setDate(now.getDate() + this.SESSION_DURATION_DAYS);

    const session: LocalSession = {
      phoneNumber,
      uid,
      displayName,
      email,
      createdAt: now.toISOString(),
      lastLoginAt: now.toISOString(),
      expiresAt: expiresAt.toISOString()
    };

    localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
    return session;
  }

  /**
   * Update last login time
   */
  updateLastLogin(): void {
    try {
      const sessionData = localStorage.getItem(this.SESSION_KEY);
      if (!sessionData) return;

      const session: LocalSession = JSON.parse(sessionData);
      session.lastLoginAt = new Date().toISOString();
      
      localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
    } catch (error) {
      console.error('Error updating last login:', error);
    }
  }

  /**
   * Get current session
   */
  getCurrentSession(): LocalSession | null {
    try {
      const sessionData = localStorage.getItem(this.SESSION_KEY);
      if (!sessionData) return null;

      const session: LocalSession = JSON.parse(sessionData);
      
      // Check if session is expired
      const expiresAt = new Date(session.expiresAt);
      if (new Date() > expiresAt) {
        this.clearSession();
        return null;
      }

      return session;
    } catch (error) {
      console.error('Error getting current session:', error);
      this.clearSession();
      return null;
    }
  }

  /**
   * Clear the current session
   */
  clearSession(): void {
    localStorage.removeItem(this.SESSION_KEY);
    localStorage.removeItem('user'); // Clear the old user data too
  }

  /**
   * Check if session is about to expire (within 3 days)
   */
  isSessionExpiringSoon(): boolean {
    const session = this.getCurrentSession();
    if (!session) return false;

    const expiresAt = new Date(session.expiresAt);
    const threeDaysFromNow = new Date();
    threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);

    return expiresAt <= threeDaysFromNow;
  }

  /**
   * Extend session by resetting the expiration date
   */
  extendSession(): boolean {
    try {
      const session = this.getCurrentSession();
      if (!session) return false;

      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + this.SESSION_DURATION_DAYS);
      
      session.expiresAt = expiresAt.toISOString();
      session.lastLoginAt = new Date().toISOString();
      
      localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
      return true;
    } catch (error) {
      console.error('Error extending session:', error);
      return false;
    }
  }

  /**
   * Get days until session expires
   */
  getDaysUntilExpiration(): number {
    const session = this.getCurrentSession();
    if (!session) return 0;

    const expiresAt = new Date(session.expiresAt);
    const now = new Date();
    const diffTime = expiresAt.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  }
}